import React, { useEffect, useState } from "react";
import { EmptyState } from "../../../../components/recharts/ReMixCharts/components/EmptyState";
import { SheetTableSkeleton } from "./SheetTableSkeleton";

const MONTH_LABELS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const ALL_MONTH_KEYS = ["1","2","3","4","5","6","7","8","9","10","11","12"];

interface HourlyRow {
  hour: number;
  time: string;
  months: Record<string, number>;
}

interface Props {
  date: string;
  fetchFn: (p?: { date?: string }) => Promise<any>;
  onDataLoaded?: (data: any) => void;
}

const TEAL = "#34656D";
const BORDER = "1px solid #E2E8F0";
const HDR_BG = "linear-gradient(#EDF4F5,#EDF4F5)";

export const ShortageSurplusHourlyTable: React.FC<Props> = ({ date, fetchFn, onDataLoaded }) => {
  const [data, setData] = useState<{ data: HourlyRow[] } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    fetchFn({ date })
      .then((d) => { setData(d); onDataLoaded?.(d); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, fetchFn]);

  if (loading) return <SheetTableSkeleton cols={12} rows={6} />;

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="flex-1 min-h-0 relative bg-white rounded-xl" style={{ border: BORDER, minHeight: 200 }}>
        <EmptyState />
      </div>
    );
  }

  const rows = data.data;
  const presentKeys = ALL_MONTH_KEYS.filter((k) => rows.some((r) => k in r.months));

  const cell: React.CSSProperties = {
    border: BORDER, textAlign: "center", verticalAlign: "middle",
    fontSize: 12, padding: "0 10px", height: 34,
  };

  const stickyTd = (even: boolean): React.CSSProperties => ({
    position: "sticky", left: 0, zIndex: 5,
    backgroundColor: even ? "#FFFFFF" : "#F7FAFB",
    color: TEAL, fontWeight: 500, fontSize: 12,
    height: 34, minWidth: 130, whiteSpace: "nowrap",
    border: BORDER, borderRight: `2px solid ${TEAL}`,
    boxShadow: "3px 0 6px rgba(52,101,109,0.08)",
    textAlign: "center", verticalAlign: "middle",
  });

  return (
    <div className="flex-1 min-h-0 rounded-xl overflow-hidden bg-white flex flex-col"
      style={{ border: BORDER, boxShadow: "0 2px 12px rgba(52,101,109,0.08)" }}>
      <div style={{ overflowX: "auto", overflowY: "auto", flex: 1, minHeight: 0 }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12, minWidth: "100%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
            <tr>
              <th style={{
                position: "sticky", top: 0, left: 0, zIndex: 22,
                backgroundImage: HDR_BG, color: TEAL, fontWeight: 700, fontSize: 12,
                height: 44, minWidth: 130,
                border: BORDER, borderRight: `2px solid ${TEAL}`,
                borderBottom: "2px solid #CAD5E2",
                boxShadow: "3px 0 6px rgba(52,101,109,0.12)",
                textAlign: "center", verticalAlign: "middle",
              }}>
                Time Slot
              </th>
              {presentKeys.map((k) => (
                <th key={k} style={{
                  position: "sticky", top: 0, zIndex: 10,
                  backgroundImage: HDR_BG, color: TEAL, fontWeight: 600, fontSize: 12,
                  height: 44, minWidth: 100,
                  border: BORDER, borderBottom: "2px solid #CAD5E2",
                  textAlign: "center", verticalAlign: "middle",
                }}>
                  {MONTH_LABELS[parseInt(k) - 1]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const even = idx % 2 === 0;
              return (
                <tr key={row.hour} style={{ backgroundColor: even ? "#FFFFFF" : "#F7FAFB" }}>
                  <td style={stickyTd(even)}>{row.time}</td>
                  {presentKeys.map((k) => {
                    const val = k in row.months ? row.months[k] : null;
                    const color = val === null
                      ? "#D4D4D8"
                      : val >= 0 ? "#059669" : "#DC2626";
                    return (
                      <td key={k} style={{ ...cell, color, fontWeight: val !== null ? 500 : 400 }}>
                        {val === null ? "–" : val === 0 ? "0" : val.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
